import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Button,
  useTheme,
  TextField,
  alpha,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";

export const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <Box component="footer">
      {/* Botão para voltar ao topo */}
      <Box
        sx={{
          position: "relative",
          height: 0,
          textAlign: "center",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          aria-label="scroll back to top"
          onClick={scrollToTop}
          sx={{
            position: "absolute",
            top: -28,
            left: "50%",
            transform: "translateX(-50%)",
            minWidth: 56,
            height: 56,
            borderRadius: "50%",
            boxShadow: theme.shadows[4],
            "&:hover": {
              transform: "translateX(-50%) translateY(-5px)",
              boxShadow: theme.shadows[8],
            },
            transition: "all 0.3s ease",
            zIndex: 10,
          }}
        >
          <KeyboardArrowUpIcon />
        </Button>
      </Box>

      {/* Seção do formulário de newsletter */}
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Elementos decorativos */}
        <Box
          sx={{
            position: "absolute",
            top: -150,
            right: -150,
            width: 300,
            height: 300,
            borderRadius: "50%",
            backgroundColor: alpha("#fff", 0.05),
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -100,
            left: -100,
            width: 200,
            height: 200,
            borderRadius: "50%",
            backgroundColor: alpha("#fff", 0.05),
            zIndex: 0,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="center"
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                component="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                Receba novidades e ofertas exclusivas
              </Typography>
              <Typography variant="body1" sx={{ mb: 0, opacity: 0.9 }}>
                Cadastre-se para receber dicas de saúde e condições especiais
                para você e sua família.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Seu e-mail"
                  variant="outlined"
                  sx={{
                    backgroundColor: alpha("#fff", 0.1),
                    borderRadius: theme.shape.borderRadius,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: alpha("#fff", 0.3),
                      },
                      "&:hover fieldset": {
                        borderColor: alpha("#fff", 0.5),
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: alpha("#fff", 0.7),
                      opacity: 1,
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  endIcon={<SendIcon />}
                  sx={{
                    py: { xs: 1.5, sm: 1.8 },
                    px: { xs: 3, sm: 4 },
                    whiteSpace: "nowrap",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  }}
                >
                  Inscrever-se
                </Button>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 1,
                  textAlign: { xs: "center", sm: "left" },
                  opacity: 0.7,
                }}
              >
                Respeitamos sua privacidade. Você pode cancelar a qualquer
                momento.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Seção principal do footer */}
      <Box
        sx={{
          py: 8,
          backgroundColor: "#1A1A2E", // Fundo escuro
          color: "white",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <HealthAndSafetyIcon
                  sx={{
                    fontSize: 32,
                    color: theme.palette.secondary.main,
                    mr: 1,
                  }}
                />
                <Typography
                  variant="h5"
                  component="div"
                  sx={{ fontWeight: 700 }}
                >
                  Vida Segura
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ mb: 3, opacity: 0.7, maxWidth: 300 }}
              >
                Somos especialistas em planos de saúde, oferecendo as melhores
                opções do mercado com atendimento personalizado e humanizado.
              </Typography>

              <Stack spacing={2} sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PhoneIcon
                    sx={{
                      mr: 1.5,
                      color: theme.palette.secondary.main,
                      fontSize: 20,
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    (11) 4002-8922
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <EmailIcon
                    sx={{
                      mr: 1.5,
                      color: theme.palette.secondary.main,
                      fontSize: 20,
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    contato@vidasegura.com.br
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOnIcon
                    sx={{
                      mr: 1.5,
                      color: theme.palette.secondary.main,
                      fontSize: 20,
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Av. Paulista, 1000, São Paulo - SP
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                <IconButton
                  aria-label="Facebook"
                  size="small"
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ffffff", 0.1),
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <FacebookIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Instagram"
                  size="small"
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ffffff", 0.1),
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <InstagramIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Twitter"
                  size="small"
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ffffff", 0.1),
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <TwitterIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="LinkedIn"
                  size="small"
                  sx={{
                    color: "white",
                    backgroundColor: alpha("#ffffff", 0.1),
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <LinkedInIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Planos
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {[
                  "Individuais",
                  "Familiares",
                  "Empresariais",
                  "Odontológicos",
                  "Sênior",
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 1.5 }}>
                    <Link
                      href="#"
                      underline="hover"
                      sx={{
                        color: alpha("#fff", 0.7),
                        "&:hover": {
                          color: theme.palette.secondary.main,
                        },
                        transition: "color 0.2s",
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Operadoras
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {[
                  "Amil",
                  "Bradesco Saúde",
                  "SulAmérica",
                  "Unimed",
                  "NotreDame Intermédica",
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 1.5 }}>
                    <Link
                      href="#"
                      underline="hover"
                      sx={{
                        color: alpha("#fff", 0.7),
                        "&:hover": {
                          color: theme.palette.secondary.main,
                        },
                        transition: "color 0.2s",
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Recursos
              </Typography>
              <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                {[
                  "Blog de Saúde",
                  "FAQ",
                  "Termos de Uso",
                  "Política de Privacidade",
                  "Contato",
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ mb: 1.5 }}>
                    <Link
                      href="#"
                      underline="hover"
                      sx={{
                        color: alpha("#fff", 0.7),
                        "&:hover": {
                          color: theme.palette.secondary.main,
                        },
                        transition: "color 0.2s",
                        fontSize: "0.9rem",
                      }}
                    >
                      {item}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                Regulamentação
              </Typography>
              <Box
                component="img"
                src="https://via.placeholder.com/120x60?text=ANS"
                alt="ANS - Agência Nacional de Saúde Suplementar"
                sx={{
                  width: 120,
                  height: 60,
                  mb: 2,
                  backgroundColor: "white",
                  p: 1,
                  borderRadius: 1,
                }}
              />
              <Typography
                variant="caption"
                sx={{ display: "block", opacity: 0.7 }}
              >
                Autorizada pela ANS
              </Typography>
              <Typography
                variant="caption"
                sx={{ display: "block", opacity: 0.7 }}
              >
                Registro ANS: 12.345-6
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Barra de copyight */}
      <Box
        sx={{
          py: 3,
          backgroundColor: "#141428", // Ainda mais escuro
          color: alpha("#fff", 0.7),
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              © {currentYear} Vida Segura. Todos os direitos reservados.
            </Typography>
            <Box>
              <Typography variant="body2">
                Desenvolvido por{" "}
                <Link
                  href="#"
                  underline="hover"
                  sx={{
                    color: theme.palette.secondary.main,
                    fontWeight: 500,
                  }}
                >
                  WebSaúde
                </Link>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
